import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import EmailCodeVerify from './EmailCodeVerify';
import PhoneAuthPanel, { MODES as PHONE_MODES } from './PhoneAuthPanel';
import UserTypeChoice from './UserTypeChoice';
import DesignerProfileFields from './DesignerProfileFields';
import ShopProfileFields from './ShopProfileFields';
import AvatarPicker from './AvatarPicker';
import { USER_TYPES, emptyDesignerProfile, validateDesignerProfile } from '../constants/designer';
import { emptyShopProfile, validateShopProfile } from '../constants/shop';
import '../pages/auth.css';

function getFirebaseError(error) {
  const map = {
    'auth/email-already-in-use':   'An account already exists with this email.',
    'auth/invalid-email':          'Please enter a valid email address.',
    'auth/weak-password':          'Password must be at least 6 characters.',
    'auth/network-request-failed': 'Network error. Check your connection.',
    'auth/too-many-requests':      'Too many attempts. Please try again later.',
    'auth/popup-closed-by-user':   'Google sign-in was cancelled.',
  };
  return map[error.code] || 'Something went wrong. Please try again.';
}

// Returns { level 0-5, color }. Label looked up via t() in render.
function getPasswordStrength(pw) {
  if (!pw)          return { level: 0, color: '#e5e7eb' };
  if (pw.length < 6) return { level: 1, color: '#ef4444' };
  let score = 1;
  if (pw.length >= 8)           score++;
  if (/[A-Z]/.test(pw))        score++;
  if (/[0-9]/.test(pw))        score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 2) return { level: 2, color: '#f97316' };
  if (score === 3) return { level: 3, color: '#eab308' };
  if (score === 4) return { level: 4, color: '#22c55e' };
  return              { level: 5, color: '#10b981' };
}

const STRENGTH_KEYS = ['', 'auth_pw_tooshort', 'auth_pw_weak', 'auth_pw_fair', 'auth_pw_good', 'auth_pw_strong'];

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path fill="#4285F4" d="M23.745 12.27c0-.79-.07-1.54-.19-2.27h-11.3v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"/>
      <path fill="#34A853" d="M12.255 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96h-3.98v3.09C3.515 21.3 7.615 24 12.255 24z"/>
      <path fill="#FBBC05" d="M5.525 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62h-3.98a11.86 11.86 0 000 10.76l3.98-3.09z"/>
      <path fill="#EA4335" d="M12.255 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C18.205 1.19 15.495 0 12.255 0c-4.64 0-8.74 2.7-10.71 6.62l3.98 3.09c.95-2.85 3.6-4.96 6.73-4.96z"/>
    </svg>
  );
}

function EyeOpen() {
  return (
    <svg viewBox="0 0 24 24" fill="none" width="18" height="18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  );
}

function EyeClosed() {
  return (
    <svg viewBox="0 0 24 24" fill="none" width="18" height="18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

// The actual registration form, shared by the standalone /register page and
// GuestRegisterModal (the "/try" flow's in-place sign-up modal) — both just
// supply what happens once an account exists via `onSuccess`.
//
// Two real steps, not one long form:
//   1. email  — just the address + the 6-digit OTP verification.
//   2. details — name + password, shown only once step 1 succeeds.
// EmailCodeVerify owns its OWN <form> (for the code input + Verify button),
// so step 1 deliberately renders it OUTSIDE any <form> of its own — nesting
// it inside this component's form used to be exactly what made clicking
// "Verify" submit/reload the page (nested <form> elements are invalid HTML
// and browsers handle them unpredictably).
export default function RegisterForm({ onSuccess, showSignInLink = true }) {
  const { register, loginWithGoogle } = useAuth();
  const { t } = useT();

  const [step, setStep] = useState('email'); // 'email' | 'type' | 'details'
  // Phone swaps out step 1 only. Like Google it is a complete sign-up on its
  // own — it produces an account with no type chosen, which `onSuccess` reports
  // as `isNewAccount` so the caller can ask that question wherever its own flow
  // asks it.
  const [usePhone, setUsePhone] = useState(false);
  // Account type, chosen between verification and details because it decides
  // WHICH details get asked for. Defaults to customer so the flow always has a
  // valid value even if the user never touches the cards.
  const [userType, setUserType] = useState(USER_TYPES.CUSTOMER);
  const [designerProfile, setDesignerProfile] = useState(() => ({
    ...emptyDesignerProfile(), avatarUrl: '',
  }));
  const [designerErrors, setDesignerErrors] = useState({});
  const [shopProfile, setShopProfile] = useState(() => ({
    ...emptyShopProfile(), avatarUrl: '',
  }));
  const [shopErrors, setShopErrors] = useState({});
  // Customers get a profile picture too — designers and shops set theirs inside
  // their profile block, so this one is only rendered on the customer path.
  const [avatarUrl, setAvatarUrl] = useState('');
  const isDesigner = userType === USER_TYPES.DESIGNER;
  const isShop     = userType === USER_TYPES.SHOP;
  // The two roles that PUBLISH a profile. Everything the details step does
  // differently for a designer, it does identically for a shop — the published
  // name replaces the account-name input, the logo replaces the avatar picker,
  // and the role's own fields carry their own validation — so the branch is on
  // "has a public profile", not on which role it is.
  const hasRoleProfile = isDesigner || isShop;

  const [name,          setName]          = useState('');
  const [email,         setEmail]         = useState('');
  const [password,      setPassword]      = useState('');
  const [confirm,       setConfirm]       = useState('');
  const [showPass,      setShowPass]      = useState(false);
  const [showConfirm,   setShowConfirm]   = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [toast,         setToast]         = useState(null);
  const [fieldErrors,   setFieldErrors]   = useState({});
  // The actual authorization gate for calling register() — set true only by
  // EmailCodeVerify's onVerified, cleared the moment the email is edited or
  // the user goes back to change it, so a code confirmed for one address can
  // never authorize creating an account under a different one.
  const [emailVerified, setEmailVerified] = useState(false);

  const strength = getPasswordStrength(password);

  function showToast(msg, type = 'error') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 5000);
  }

  // Only name/password/confirm — the email itself was already format-checked
  // and confirmed real back in step 1.
  function validate() {
    const errors = {};
    // A designer's or shop's name lives in the profile block (it's the
    // published name), so the plain account-name input isn't rendered for them —
    // validating it here would fail on a field the user was never shown.
    if (!hasRoleProfile && (!name.trim() || name.trim().length < 2)) errors.name = t('auth_err_name');
    if (!password)                               errors.password = t('auth_err_pass_req');
    else if (password.length < 6)               errors.password = t('auth_err_pass_min');
    if (!confirm)                                errors.confirm  = t('auth_err_conf_req');
    else if (confirm !== password)               errors.confirm  = t('auth_err_conf_match');
    setFieldErrors(errors);

    // The chosen role validates its own profile — each shape has its own rules,
    // and neither is asked about the other's fields.
    let rErrors = {};
    if (isDesigner) {
      rErrors = validateDesignerProfile(designerProfile);
      setDesignerErrors(rErrors);
    } else if (isShop) {
      rErrors = validateShopProfile(shopProfile);
      setShopErrors(rErrors);
    }
    return Object.keys(errors).length === 0 && Object.keys(rErrors).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    // Defensive — the details step/form can't normally be reached without
    // this already being true, but never trust that alone.
    if (!emailVerified) { showToast(t('auth_verify_email_gate')); setStep('email'); return; }
    if (!validate()) return;
    setLoading(true);
    setToast(null);
    try {
      // For a designer or a shop the published profile name IS the account name.
      const accountName = isDesigner ? designerProfile.fullName.trim()
        : isShop ? shopProfile.name.trim()
          : name.trim();
      const roleProfile = isDesigner ? designerProfile : isShop ? shopProfile : null;
      // The picture rides along inside the role profile for those two; only a
      // customer's arrives separately.
      await register(email.trim(), password, accountName, userType,
        roleProfile, hasRoleProfile ? '' : avatarUrl);
      showToast('Account created!', 'success');
      // `userType` is reported so the caller can route on it. A designer and a
      // shop have just filled their whole profile in on this form; a customer
      // has given a name and a picture but has never been asked for a location,
      // so /register sends them on to finish it — see Register.jsx.
      setTimeout(() => onSuccess({ userType }), 600);
    } catch (err) {
      showToast(getFirebaseError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
    setGoogleLoading(true);
    setToast(null);
    try {
      // `isNewAccount` is handed to the caller rather than acted on here, because
      // what to do about it is the caller's business: /register sends a new
      // account to the customer-or-designer page, while the guest modal has its
      // own next step and lets ProtectedRoute ask the question later.
      const { isNewAccount } = await loginWithGoogle();
      onSuccess({ isNewAccount });
    } catch (err) {
      showToast(getFirebaseError(err));
    } finally {
      setGoogleLoading(false);
    }
  }

  function handleChangeEmail() {
    setStep('email');
    setEmailVerified(false);
  }

  const busy = loading || googleLoading;

  // THE PROFILE STEP GETS THE SETTINGS PAGE'S WIDTH. A designer's or shop's
  // profile block is the same component the settings card renders, and it was
  // being folded into the 440px sign-in column: three grid columns inside a
  // third of their width, with the name field crushed to a sliver. Only this
  // step widens — the email and account-type steps are better narrow.
  const wideStep = step === 'details' && hasRoleProfile;

  return (
    <div className={wideStep ? 'rf-wide' : undefined}>
      {/* Header */}
      <div className="af-header">
        <h2 className="af-title">{t('auth_register_title')}</h2>
        <p className="af-subtitle">
          {step === 'email' ? t('auth_verify_step_sub')
            : step === 'type' ? t('auth_type_step_sub')
            : t('auth_details_step_sub')}
        </p>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`af-toast af-toast--${toast.type}`} role="alert">
          <span className="af-toast__icon">{toast.type === 'error' ? '⚠' : '✓'}</span>
          {toast.msg}
        </div>
      )}

      {step === 'email' ? (
        <>
          {/* Google — a one-click alternative that skips the OTP step entirely. */}
          <button className="af-google-btn" onClick={handleGoogle} disabled={busy} type="button">
            {googleLoading ? <span className="af-spinner" /> : <GoogleIcon />}
            {t('auth_google_signup')}
          </button>

          {/* Phone — the other one. Its OTP is an SMS rather than an email. */}
          <button
            className="af-google-btn af-phone-btn"
            onClick={() => { setUsePhone(true); setToast(null); }}
            disabled={busy || usePhone}
            type="button"
          >
            <svg viewBox="0 0 24 24" fill="none" width="19" height="19" stroke="currentColor"
              strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="6" y="2" width="12" height="20" rx="2.5" />
              <path d="M11 18.5h2" />
            </svg>
            {t('auth_phone_signup')}
          </button>

          <div className="af-divider">
            {usePhone ? t('auth_or_phone_signup') : t('auth_or_email_signup')}
          </div>

          {usePhone && (
            <PhoneAuthPanel
              mode={PHONE_MODES.SIGN_UP}
              onDone={({ isNewAccount }) => onSuccess({ isNewAccount })}
              onCancel={() => setUsePhone(false)}
            />
          )}

          {/* Step 1: email + OTP. Intentionally NOT wrapped in a <form> — see
              the component-level comment above. Hidden while the phone panel is
              open: they are two ways of proving the same thing, and the reader
              should be answering one question, not choosing between two forms. */}
          {!usePhone && (
          <>
          <div className="af-field">
            <label className="af-label" htmlFor="reg-email">{t('auth_email_label')}</label>
            <div className="af-input-wrap">
              <span className="af-input-icon">
                <svg viewBox="0 0 24 24" fill="none" width="16" height="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </span>
              <input
                id="reg-email"
                className={`af-input${fieldErrors.email ? ' af-input--error' : ''}`}
                type="email"
                placeholder={t('auth_email_ph')}
                value={email}
                onChange={e => {
                  setEmail(e.target.value);
                  setFieldErrors(p => ({ ...p, email: '' }));
                  setEmailVerified(false);
                }}
                autoComplete="email"
                disabled={busy}
                autoFocus
              />
            </div>
            {fieldErrors.email && <p className="af-field-error">{fieldErrors.email}</p>}
          </div>

          <EmailCodeVerify
            email={email.trim()}
            onVerified={() => { setEmailVerified(true); setStep('type'); }}
          />
          </>
          )}
        </>
      ) : step === 'type' ? (
        <>
          {/* Step 2: account type. Its own step rather than a field on the
              details form, because the answer changes what that form asks. */}
          <UserTypeChoice value={userType} onChange={setUserType} disabled={busy} />
          {/* `af-btn` is the app's primary button class (see auth.css). An
              earlier "af-submit" here matched no rule at all, so this rendered
              as an unstyled native button. */}
          <button
            type="button"
            className="af-btn"
            style={{ marginTop: 18 }}
            onClick={() => setStep('details')}
            disabled={busy}
          >
            {t('auth_continue')}
          </button>
        </>
      ) : (
        <>
          {/* Verified email — read-only, with a way back to correct it. */}
          <div className="af-field">
            <label className="af-label">{t('auth_email_label')}</label>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', borderRadius: 10,
              background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.25)',
            }}>
              <span style={{ color: '#10b981', fontWeight: 700 }}>✓</span>
              <span style={{ fontSize: '0.85rem', color: 'var(--site-text-1)', fontWeight: 600, flex: 1, wordBreak: 'break-all' }}>
                {email.trim()}
              </span>
              <button
                type="button"
                onClick={handleChangeEmail}
                style={{ background: 'none', border: 'none', color: '#6366F1', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                {t('auth_change_email')}
              </button>
            </div>
          </div>

          {/* Step 3: details + password — the only <form> in this component. */}
          <form onSubmit={handleSubmit} noValidate>

            {/* Full Name — customers only. A designer's or shop's name is the
                REQUIRED field inside the profile block below and gets
                published, so showing both would be two inputs asking the same
                question. */}
            {!hasRoleProfile && (
            <div className="af-field">
              <AvatarPicker value={avatarUrl} onChange={setAvatarUrl} disabled={busy} />
            </div>
            )}

            {!hasRoleProfile && (
            <div className="af-field">
              <label className="af-label" htmlFor="reg-name">{t('auth_name_label')}</label>
              <div className="af-input-wrap">
                <span className="af-input-icon">
                  <svg viewBox="0 0 24 24" fill="none" width="16" height="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                </span>
                <input
                  id="reg-name"
                  className={`af-input${fieldErrors.name ? ' af-input--error' : ''}`}
                  type="text"
                  placeholder={t('auth_name_ph')}
                  value={name}
                  onChange={e => { setName(e.target.value); setFieldErrors(p => ({ ...p, name: '' })); }}
                  autoComplete="name"
                  disabled={busy}
                  autoFocus
                />
              </div>
              {fieldErrors.name && <p className="af-field-error">{fieldErrors.name}</p>}
            </div>
            )}

            {/* Designer profile — every field except the name is optional, so
                signup stays short for anyone who wants to fill it in later
                from Settings. */}
            {isDesigner && (
              <div className="af-field">
                <DesignerProfileFields
                  value={designerProfile}
                  onChange={p => { setDesignerProfile(p); setDesignerErrors({}); }}
                  errors={designerErrors}
                  disabled={busy}
                  // True because `wideStep` widened the panel to match the
                  // settings card. Reading the same flag keeps the width and
                  // the layout from disagreeing.
                  roomy={wideStep}
                />
              </div>
            )}

            {/* Shop profile — name and at least one category are required
                (that pair is what makes a shop findable at all); everything
                else can be filled in later from Settings. */}
            {isShop && (
              <div className="af-field">
                <ShopProfileFields
                  value={shopProfile}
                  onChange={p => { setShopProfile(p); setShopErrors({}); }}
                  errors={shopErrors}
                  disabled={busy}
                  roomy={wideStep}
                />
              </div>
            )}

            {/* Password */}
            <div className="af-field">
              <label className="af-label" htmlFor="reg-password">{t('auth_password_label')}</label>
              <div className="af-input-wrap">
                <span className="af-input-icon">
                  <svg viewBox="0 0 24 24" fill="none" width="16" height="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0110 0v4"/>
                  </svg>
                </span>
                <input
                  id="reg-password"
                  className={`af-input af-input--has-action${fieldErrors.password ? ' af-input--error' : ''}`}
                  type={showPass ? 'text' : 'password'}
                  placeholder={t('auth_password_min_ph')}
                  value={password}
                  onChange={e => { setPassword(e.target.value); setFieldErrors(p => ({ ...p, password: '' })); }}
                  autoComplete="new-password"
                  disabled={busy}
                />
                <button type="button" className="af-input-action" onClick={() => setShowPass(s => !s)} tabIndex={-1}>
                  {showPass ? <EyeClosed /> : <EyeOpen />}
                </button>
              </div>
              {fieldErrors.password && <p className="af-field-error">{fieldErrors.password}</p>}

              {/* Strength indicator */}
              {password.length > 0 && (
                <div className="af-strength">
                  <div className="af-strength-bars">
                    {[1, 2, 3, 4].map(n => (
                      <div
                        key={n}
                        className="af-strength-bar"
                        style={{ background: strength.level >= n + 1 ? strength.color : 'var(--site-border-2)' }}
                      />
                    ))}
                  </div>
                  <span className="af-strength-label" style={{ color: strength.level > 1 ? strength.color : 'var(--site-text-3)' }}>
                    {t(STRENGTH_KEYS[strength.level])}
                  </span>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="af-field">
              <label className="af-label" htmlFor="reg-confirm">{t('auth_confirm_label')}</label>
              <div className="af-input-wrap">
                <span className="af-input-icon">
                  <svg viewBox="0 0 24 24" fill="none" width="16" height="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                </span>
                <input
                  id="reg-confirm"
                  className={`af-input af-input--has-action${fieldErrors.confirm ? ' af-input--error' : ''}`}
                  type={showConfirm ? 'text' : 'password'}
                  placeholder={t('auth_confirm_ph')}
                  value={confirm}
                  onChange={e => { setConfirm(e.target.value); setFieldErrors(p => ({ ...p, confirm: '' })); }}
                  autoComplete="new-password"
                  disabled={busy}
                />
                <button type="button" className="af-input-action" onClick={() => setShowConfirm(s => !s)} tabIndex={-1}>
                  {showConfirm ? <EyeClosed /> : <EyeOpen />}
                </button>
              </div>
              {fieldErrors.confirm && <p className="af-field-error">{fieldErrors.confirm}</p>}
            </div>

            <button className="af-btn" type="submit" disabled={busy}>
              {loading && <span className="af-spinner" />}
              {loading ? t('auth_signup_ing') : t('auth_signup_btn')}
            </button>
          </form>
        </>
      )}

      {showSignInLink && (
        <p className="af-footer">
          {t('auth_have_account')}{' '}
          <Link to="/login">{t('auth_signin_link')}</Link>
        </p>
      )}
    </div>
  );
}
